import {
    Button,
    Col,
    Row,
    Image,
    List,
    Card,
    Input,
    Result,
    Pagination,
    Select,
    Spin,
    Skeleton,
} from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { recordError } from "../../utils/logService";
import {
    UserOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import service from "../../services/volunteerScientistService";

/**
 * - masterList: large client-side copy used for dropdown availability & pre-search filtering
 * - page results: fetched from server for initial display & for searches
 * - searchGuidedList: results returned from a search (then filters apply client-side to this list)
 *
 * Note: To fetch masterList we call service.getVolunteerScientists(0, MASTER_FETCH_SIZE)
 *       If your backend supports a dedicated endpoint for full lists, swap to that.
 */

const MASTER_FETCH_SIZE = 100000; // adjust if needed for realistic maximum

export default function VolunteerScientistHome() {
    const [masterList, setMasterList] = useState([]); // full dataset for dropdowns and pre-search filters
    const [pagedResults, setPagedResults] = useState([]); // data returned directly from server (page)
    const [searchGuidedList, setSearchGuidedList] = useState(null); // null = no search performed; otherwise array
    const [activeFilteredList, setActiveFilteredList] = useState([]); // list after applying filters (client-side)
    const [filteredData, setFilteredData] = useState([]); // actual slice displayed in list
    const [isLoading, setIsLoading] = useState(true);
    const [loading, setLoading] = useState(false);

    // search & pagination states
    const [searchText, setSearchText] = useState("");
    const [isNotSearchable, setIsNotSearchable] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [firstRecord, setFirstRecord] = useState(0);
    const [total, setTotal] = useState({ original: 0, updated: 0 });
    const [isEmptyResults, setIsEmptyResults] = useState(false);

    // filters (values shown in selects)
    const [filterFieldOfStudy, setFilterFieldOfStudy] = useState("All Field of study");
    const [filterAreaOfSpecialisation, setFilterAreaOfSpecialisation] = useState("All area of specialisation");
    const [filterAgeRange, setFilterAgeRange] = useState("All age ranges");
    const [filterProvince, setFilterProvince] = useState("All Provinces");
    const [filterLanguage, setFilterLanguage] = useState("All Languages");

    // select options coming from server endpoints (populates selects)
    const [fieldOfStudyOptions, setFieldOfStudyOptions] = useState([]);
    const [areaOfSpecialisationOptions, setAreaOfSpecialisationOptions] = useState([]);
    const [ageRangeOptions, setAgeRangeOptions] = useState([]);
    const [languageOptions, setLanguageOptions] = useState([]);
    const [provinceOptions, setProvinceOptions] = useState([]);

    // availability sets derived from masterList or searchGuidedList
    const [availableFieldOfStudy, setAvailableFieldOfStudy] = useState([]);
    const [availableAreaOfSpecialisation, setAvailableAreaOfSpecialisation] = useState([]);
    const [availableAgeRange, setAvailableAgeRange] = useState([]);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [availableProvinces, setAvailableProvinces] = useState([]);

    const location = useLocation();
    const { Option } = Select;

    // helper: normalize strings for comparisons
    const normalizeString = (str) => (str ? String(str).trim().toLowerCase() : "");

    // Compute available options from a list (masterList or searchGuidedList)
    const calculateAvailableOptions = (list = []) => {
        const sets = {
            fieldOfStudy: new Set(),
            areaOfSpecialisation: new Set(),
            ageRange: new Set(),
            language: new Set(),
            province: new Set(),
        };

        list.forEach((item) => {
            if (item.fieldOfStudy) sets.fieldOfStudy.add(normalizeString(item.fieldOfStudy));
            if (item.specialisation) sets.areaOfSpecialisation.add(normalizeString(item.specialisation));
            if (item.ageRange) sets.ageRange.add(normalizeString(item.ageRange));
            // if (item.language) {
            //     getLanguageArray(item.language).forEach(l => sets.language.add(l));
            // }
            if (item.language) {
                item.language
                    .split(",")
                    .map(x => normalizeString(x.trim()))
                    .forEach(lang => sets.language.add(lang));
            }
            if (item.province) sets.province.add(normalizeString(item.province));
        });

        return {
            availableFieldOfStudy: Array.from(sets.fieldOfStudy),
            availableAreaOfSpecialisation: Array.from(sets.areaOfSpecialisation),
            availableAgeRange: Array.from(sets.ageRange),
            availableLanguages: Array.from(sets.language),
            availableProvinces: Array.from(sets.province),
        };
    };
    //Normalize volunteer languages into arrays
    const getLanguageArray = (lang) => {
        if (!lang) return [];
        return lang.split(",").map(l => normalizeString(l));
    };

    // initial load: fetch masterList (for dropdowns & client filtering) and first page
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                // 1) Fetch master list (large) for dropdown availability & pre-search filtering
                try {
                    const respondAll = await service.getVolunteerScientists(0, MASTER_FETCH_SIZE, null);
                    if (respondAll && respondAll.status === 200) {
                        if (!mounted) return;
                        const allResults = respondAll.data?.results || [];
                        setMasterList(allResults);
                        // set original total count from server if provided
                        setTotal((prev) => ({ ...prev, original: respondAll.data?.total ?? allResults.length }));
                        const avail = calculateAvailableOptions(allResults);
                        setAvailableFieldOfStudy(avail.availableFieldOfStudy);
                        setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
                        setAvailableAgeRange(avail.availableAgeRange);
                        setAvailableLanguages(avail.availableLanguages);
                        setAvailableProvinces(avail.availableProvinces);
                    }
                } catch (e) {
                    // If fetching full list is heavy or fails, we continue — dropdowns may come from dedicated endpoints below.
                    recordError(e);
                }

                // 2) Fetch select dropdown option lists (these provide the human-readable labels)
                await Promise.all([
                    fetchFieldOfStudyOptions(),
                    fetchAreaOfSpecialisationOptions(),
                    fetchAgeRangesOptions(),
                    fetchLanguageOptions(),
                    fetchProvinceOptions(),
                ]);

                // 3) Fetch first page for display (server-side)
                await loadServerPage({ first: 0, max: pageSize, searchValue: null });
            } catch (err) {
                recordError(err);
            } finally {
                if (mounted) setIsLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Utility to fetch server page (used for initial load and searches)
    const loadServerPage = async ({ first = 0, max = 10, searchValue = null }) => {
        setLoading(true);
        try {
            const response = searchValue ? await service.getVolunteerScientists(first, max, searchValue) : await service.getVolunteerScientists(first, max);
            if (response && response.status === 200) {
                const results = response.data?.results || [];
                setPagedResults(results);
                setFilteredData(results);
                setTotal({ original: response.data?.total ?? results.length, updated: response.data?.total ?? results.length });
                setIsEmptyResults(results.length === 0);
            } else {
                setPagedResults([]);
                setFilteredData([]);
                setIsEmptyResults(true);
            }
        } catch (err) {
            recordError(err);
            setPagedResults([]);
            setFilteredData([]);
            setIsEmptyResults(true);
        } finally {
            setLoading(false);
        }
    };

    // Fetch dropdown option lists (human readable values)
    const fetchFieldOfStudyOptions = async () => {
        try {
            const response = await service.getFieldOfStudy();
            if (response && response.status === 200) {
                const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
                const all = [{ key: -1, value: "All Field of study" }, ...data];
                setFieldOfStudyOptions(all);
            }
        } catch (err) {
            recordError(err);
        }
    };
    const fetchAreaOfSpecialisationOptions = async () => {
        try {
            const response = await service.getAreOfSpecialisation();
            if (response && response.status === 200) {
                const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
                const all = [{ key: -1, value: "All area of specialisation" }, ...data];
                setAreaOfSpecialisationOptions(all);
            }
        } catch (err) {
            recordError(err);
        }
    };
    const fetchAgeRangesOptions = async () => {
        try {
            const response = await service.getAgeRanges();
            if (response && response.status === 200) {
                const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
                const all = [{ key: -1, value: "All age ranges" }, ...data];
                setAgeRangeOptions(all);
            }
        } catch (err) {
            recordError(err);
        }
    };
    const fetchLanguageOptions = async () => {
        try {
            const response = await service.getLanguages();
            if (response && response.status === 200) {
                const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
                const all = [{ key: -1, value: "All Languages" }, ...data];
                setLanguageOptions(all);
            }
        } catch (err) {
            recordError(err);
        }
    };
    const fetchProvinceOptions = async () => {
        try {
            const response = await service.getProvinces();
            if (response && response.status === 200) {
                const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
                const all = [{ key: -1, value: "All Provinces" }, ...data];
                setProvinceOptions(all);
            }
        } catch (err) {
            recordError(err);
        }
    };
    // Perform a server-side search; the returned results become searchGuidedList
    const handleSearch = async () => {
        if (!searchText || searchText.trim() === "") return;
        setIsNotSearchable(false);
        setLoading(true);

        try {
            // 1) First request: get the first page to obtain total count
            const firstResp = await service.getVolunteerScientists(0, pageSize, searchText.trim());
            if (!firstResp || firstResp.status !== 200) {
                // nothing found or error
                setSearchGuidedList([]);
                setActiveFilteredList([]);
                setFilteredData([]);
                setTotal((prev) => ({ ...prev, updated: 0 }));
                setIsEmptyResults(true);
                return;
            }

            const totalHits = firstResp.data?.total ?? firstResp.data?.results?.length ?? 0;

            if (totalHits === 0) {
                // nothing found
                setSearchGuidedList([]);
                setActiveFilteredList([]);
                setFilteredData([]);
                setTotal((prev) => ({ ...prev, updated: 0 }));
                setIsEmptyResults(true);
                return;
            }

            // 2) If totalHits <= pageSize we already have all results in firstResp
            if (totalHits <= firstResp.data.results.length) {
                const results = firstResp.data.results || [];
                setSearchGuidedList(results);
                setActiveFilteredList(results);
                const avail = calculateAvailableOptions(results);
                setAvailableFieldOfStudy(avail.availableFieldOfStudy);
                setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
                setAvailableAgeRange(avail.availableAgeRange);
                setAvailableLanguages(avail.availableLanguages);
                setAvailableProvinces(avail.availableProvinces);
                setTotal((prev) => ({ ...prev, updated: results.length }));
                setFilteredData(results.slice(0, pageSize));
                setCurrentPage(1);
                setFirstRecord(0);
                setIsEmptyResults(results.length === 0);
            } else {
                // 3) Fetch ALL results in one go (if your backend supports a big 'max' parameter)
                //    or loop through pages if backend imposes max limits.
                // Try single big fetch first:
                const allResp = await service.getVolunteerScientists(0, totalHits, searchText.trim());
                if (allResp && allResp.status === 200) {
                    const results = allResp.data?.results || [];
                    setSearchGuidedList(results);
                    setActiveFilteredList(results);
                    const avail = calculateAvailableOptions(results);
                    setAvailableFieldOfStudy(avail.availableFieldOfStudy);
                    setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
                    setAvailableAgeRange(avail.availableAgeRange);
                    setAvailableLanguages(avail.availableLanguages);
                    setAvailableProvinces(avail.availableProvinces);
                    setTotal((prev) => ({ ...prev, updated: results.length }));
                    setFilteredData(results.slice(0, pageSize));
                    setCurrentPage(1);
                    setFirstRecord(0);
                    setIsEmptyResults(results.length === 0);
                } else {
                    // Fallback: if server won't respond to huge max, page the requests
                    let results = [...firstResp.data.results];
                    let fetched = results.length;
                    let start = fetched;
                    while (fetched < totalHits) {
                        const chunk = await service.getVolunteerScientists(start, pageSize, searchText.trim());
                        if (!chunk || chunk.status !== 200) break;
                        const rr = chunk.data?.results || [];
                        results = results.concat(rr);
                        fetched = results.length;
                        start = fetched;
                        // safety guard
                        if (rr.length === 0) break;
                    }
                    setSearchGuidedList(results);
                    setActiveFilteredList(results);
                    const avail = calculateAvailableOptions(results);
                    setAvailableFieldOfStudy(avail.availableFieldOfStudy);
                    setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
                    setAvailableAgeRange(avail.availableAgeRange);
                    setAvailableLanguages(avail.availableLanguages);
                    setAvailableProvinces(avail.availableProvinces);
                    setTotal((prev) => ({ ...prev, updated: results.length }));
                    setFilteredData(results.slice(0, pageSize));
                    setCurrentPage(1);
                    setFirstRecord(0);
                    setIsEmptyResults(results.length === 0);
                }
            }
        } catch (err) {
            recordError(err);
            setSearchGuidedList([]);
            setActiveFilteredList([]);
            setFilteredData([]);
            setIsEmptyResults(true);
        } finally {
            setLoading(false);
        }
    };
    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchText(val);
        setIsNotSearchable(!val || val.trim().length === 0);
        // if user clears the search string, clear the search-guided dataset and fallback to server paged results
        if (!val || val.trim() === "") {
            setSearchGuidedList(null);
            // reset availability to masterList
            const avail = calculateAvailableOptions(masterList);
            setAvailableFieldOfStudy(avail.availableFieldOfStudy);
            setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
            setAvailableAgeRange(avail.availableAgeRange);
            setAvailableLanguages(avail.availableLanguages);
            setAvailableProvinces(avail.availableProvinces);
            // reload server page
            loadServerPage({ first: 0, max: pageSize, searchValue: null });
        }
    };
    // clear filters button - resets search and filters to default and loads server page
    const clearFilters = (e) => {
        e.preventDefault();
        setFilterFieldOfStudy("All Field of study");
        setFilterAreaOfSpecialisation("All area of specialisation");
        setFilterAgeRange("All age ranges");
        setFilterLanguage("All Languages");
        setFilterProvince("All Provinces");
        setSearchText("");
        setIsNotSearchable(true);
        setSearchGuidedList(null);
        setActiveFilteredList([]);
        setFilteredData([]);
        setCurrentPage(1);
        setFirstRecord(0);
        // reset availability from masterList
        const avail = calculateAvailableOptions(masterList);
        setAvailableFieldOfStudy(avail.availableFieldOfStudy);
        setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
        setAvailableAgeRange(avail.availableAgeRange);
        setAvailableLanguages(avail.availableLanguages);
        setAvailableProvinces(avail.availableProvinces);
        loadServerPage({ first: 0, max: pageSize, searchValue: null });
    };
    // Core: apply filters to the dataset (masterList when no searchGuidedList; otherwise searchGuidedList)
    const applyFiltersClientSide = (overrides = {}) => {
        // Build effective filter values (overrides allow using pagination changes without losing filters)
        const effField = overrides.fieldOfStudy ?? filterFieldOfStudy;
        const effArea = overrides.areaOfSpecialisation ?? filterAreaOfSpecialisation;
        const effAge = overrides.ageRange ?? filterAgeRange;
        const effLang = overrides.language ?? filterLanguage;
        const effProv = overrides.province ?? filterProvince;

        // choose the base list to filter
        const base = searchGuidedList !== null ? searchGuidedList : masterList;

        let copy = [...base];

        if (effField && effField !== "All Field of study") {
            copy = copy.filter((x) => normalizeString(x.fieldOfStudy) === normalizeString(effField));
        }
        if (effArea && effArea !== "All area of specialisation") {
            copy = copy.filter((x) => normalizeString(x.specialisation) === normalizeString(effArea));
        }
        if (effAge && effAge !== "All age ranges") {
            copy = copy.filter((x) => normalizeString(x.ageRange) === normalizeString(effAge));
        }
        if (effLang && effLang !== "All Languages") {
            const target = normalizeString(effLang);
            copy = copy.filter((x) => {
                const langs = getLanguageArray(x.language);
                return langs.includes(target);
            });
        }

        if (effProv && effProv !== "All Provinces") {
            copy = copy.filter((x) => normalizeString(x.province) === normalizeString(effProv));
        }

        // update availability depending on current base (if filtering after search use the searchGuidedList as base)
        const avail = calculateAvailableOptions(base);
        setAvailableFieldOfStudy(avail.availableFieldOfStudy);
        setAvailableAreaOfSpecialisation(avail.availableAreaOfSpecialisation);
        setAvailableAgeRange(avail.availableAgeRange);
        setAvailableLanguages(avail.availableLanguages);
        setAvailableProvinces(avail.availableProvinces);

        // update active filtered list and pagination
        setActiveFilteredList(copy);
        setTotal((prev) => ({ ...prev, updated: copy.length }));
        setCurrentPage(1);
        setFirstRecord(0);
        setFilteredData(copy.slice(0, pageSize));
        setIsEmptyResults(copy.length === 0);
    };
    // When a filter changes - we update the corresponding state and reapply filters client-side
    const onFieldOfStudyChange = (val) => {
        setLoading(true);
        setFilterFieldOfStudy(val);
        applyFiltersClientSide({ fieldOfStudy: val });
        setLoading(false);
    };
    const onAreaOfSpecialisatioChange = (val) => {
        setLoading(true);
        setFilterAreaOfSpecialisation(val);
        applyFiltersClientSide({ areaOfSpecialisation: val });
        setLoading(false);
    };
    const onAgeRangeChange = (val) => {
        setLoading(true);
        setFilterAgeRange(val);
        applyFiltersClientSide({ ageRange: val });
        setLoading(false);
    };
    const onLanguageChange = (val) => {
        setLoading(true);

        // If user selects All Languages
        if (val === "All Languages") {
            setFilterLanguage("All Languages");
            applyFiltersClientSide({ language: "All Languages" });
            setLoading(false);
            return;
        }

        // User selects a specific language -> remove All Languages selection
        if (filterLanguage === "All Languages") {
            setFilterLanguage(val);
            applyFiltersClientSide({ language: val });
            setLoading(false);
            return;
        }

        // Normal select
        setFilterLanguage(val);
        applyFiltersClientSide({ language: val });
        setLoading(false);
    };
    const onProvinceChange = (val) => {
        setLoading(true);
        setFilterProvince(val);
        applyFiltersClientSide({ province: val });
        setLoading(false);
    };
    // pagination change: if we are showing client-side filtered results (activeFilteredList exists),
    // paginate locally. Otherwise request server page.
    const onPageChange = (page, size) => {
        setLoading(true);
        const keepCurrentPage = pageSize === size;
        const first = keepCurrentPage ? (page - 1) * pageSize : 0;
        const newPage = keepCurrentPage ? page : 1;
        setFirstRecord(first);
        setPageSize(size);
        setCurrentPage(newPage);

        if (activeFilteredList && activeFilteredList.length > 0) {
            // client-side pagination
            const slice = activeFilteredList.slice(first, first + size);
            setFilteredData(slice);
            setLoading(false);
        } else if (searchGuidedList !== null) {
            // if searchGuidedList exists but not currently filtered (activeFilteredList empty), paginate that client-side
            const base = searchGuidedList;
            const slice = base.slice(first, first + size);
            setFilteredData(slice);
            setLoading(false);
        } else {
            // no local filtered list -> request server page
            loadServerPage({ first, max: size, searchValue: searchText || null });
        }
    };
    const onShowSizeChange = (page, size) => {
        // When page size changes, reset to first page
        setLoading(true);
        setPageSize(size);
        setCurrentPage(1);
        setFirstRecord(0);
        // Reuse onPageChange behavior
        onPageChange(1, size);
    };
    // If location.state contains restored pagination or filters, restore them (similar to previous behavior)
    useEffect(() => {
        if (location?.state) {
            const { currentPage: cPage, pageSize: pSize, filterProvince: fProvince } = location.state;
            if (typeof cPage === "number" && typeof pSize === "number") {
                setPageSize(pSize);
                setCurrentPage(cPage);
                const restoredFirst = (cPage - 1) * pSize;
                setFirstRecord(restoredFirst);
                if (fProvince) {
                    setFilterProvince(fProvince);
                }
                // Trigger a server page load for restored page
                loadServerPage({ first: restoredFirst, max: pSize, searchValue: null });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);
    // render helpers for Option disabled state based on availability
    const isOptionDisabled = (normalizedLabel, availableArray, allLabelNormalized) => {
        if (normalizedLabel === allLabelNormalized) return false;
        return !availableArray.includes(normalizedLabel);
    };

    // UI
    return (
        <div className="card-main report-content volunteer-search">
            {isLoading && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        zIndex: 1000,
                        flexDirection: "column",
                    }}
                >
                    <Spin size="large" />
                    <p style={{ marginTop: 20, fontSize: 20 }}>
                        <b>Please wait while the list is being loaded....</b>
                    </p>
                </div>
            )}

            <Row>
                <Col xs={24}>
                    <h1 className="center-align">Volunteer Scientists/STEM Professionals</h1>
                </Col>
            </Row>

            <Card className="doc-repo-card repo-search" bordered={false} style={{ marginBottom: "20px" }}>
                <Row className="doc-repo-search">
                    <Col className="filter-control" flex="1">
                        <span style={{ fontSize: "15px", color: "#000" }}>
                            <b>Search Terms</b>&nbsp;<i>(space separated)</i>
                        </span>
                        <Input
                            placeholder="Search by Name, Surname, City, Province, Preferred Volunteer Role"
                            type="text"
                            onChange={handleInputChange}
                            value={searchText}
                            allowClear
                            size="large"
                            prefix={<SearchOutlined />}
                            style={{ marginLeft: "0px" }}
                        />
                    </Col>

                    <Col className="filter-button">
                        <span></span> <br />
                        <Button
                            disabled={isNotSearchable}
                            key="Search"
                            value="Search"
                            type="primary"
                            onClick={handleSearch}
                            style={{ marginLeft: 10 }}
                        >
                            Search
                        </Button>
                    </Col>
                </Row>

                <br />

                <Row className="filter-selections">
                    <Col xs={24} style={{ marginLeft: 10, marginBottom: 0 }}>
                        <h3 style={{ marginBottom: 0, fontWeight: "bold", color: "#f1f1f1" }}>Filters</h3>
                    </Col>

                    <Col className="filter-control" key={"f-study"}>
                        <span style={{ fontSize: "15px", color: "white" }}>
                            <b>Field of Study</b>
                        </span>
                        <Select style={{ marginLeft: "0px", color: "white" }} onChange={onFieldOfStudyChange} value={filterFieldOfStudy}>
                            {fieldOfStudyOptions?.map((option) => {
                                const normalizedOption = normalizeString(option.value);
                                const allNormalized = normalizeString("All Field of study");
                                const disabled = isOptionDisabled(normalizedOption, availableFieldOfStudy, allNormalized);
                                return (
                                    <Option key={option.key} value={option.value} disabled={disabled}>
                                        {option.value}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Col>

                    <Col className="filter-control" style={{ marginLeft: 10 }} key={"f-specialisation"}>
                        <span style={{ fontSize: "15px", color: "white" }}>
                            <b>Area of Specialisation</b>
                        </span>
                        <Select style={{ marginLeft: "0px" }} onChange={onAreaOfSpecialisatioChange} value={filterAreaOfSpecialisation}>
                            {areaOfSpecialisationOptions?.map((option) => {
                                const normalizedOption = normalizeString(option.value);
                                const allNormalized = normalizeString("All area of specialisation");
                                const disabled = isOptionDisabled(normalizedOption, availableAreaOfSpecialisation, allNormalized);
                                return (
                                    <Option key={option.key} value={option.value} disabled={disabled}>
                                        {option.value}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Col>

                    <Col className="filter-control" style={{ marginLeft: 10 }} key={"f-years"}>
                        <span style={{ fontSize: "15px", color: "white" }}>
                            <b>Age (Range)</b>
                        </span>
                        <br />
                        <Select style={{ marginLeft: "0px" }} onChange={onAgeRangeChange} value={filterAgeRange}>
                            {ageRangeOptions?.map((option) => {
                                const normalizedOption = normalizeString(option.value);
                                const allNormalized = normalizeString("All age ranges");
                                const disabled = isOptionDisabled(normalizedOption, availableAgeRange, allNormalized);
                                return (
                                    <Option key={option.key} value={option.value} disabled={disabled}>
                                        {option.value}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Col>

                    <Col className="filter-control" style={{ marginLeft: 10 }} key={"f-languages"}>
                        <span style={{ fontSize: "15px", color: "white" }}>
                            <b>Languages</b>
                        </span>
                        <br />
                        <Select
                            style={{ marginLeft: "0px" }}
                            onChange={onLanguageChange}
                            value={filterLanguage}
                            optionLabelProp="label"
                            menuItemSelectedIcon={<span>✔</span>}
                        >
                            {languageOptions?.map((option) => {
                                const normalizedOption = normalizeString(option.value);
                                const allNormalized = normalizeString("All Languages");

                                // disable options that do not appear in search-guided list
                                const disabled = isOptionDisabled(
                                    normalizedOption,
                                    availableLanguages,
                                    allNormalized
                                );

                                return (
                                    <Option
                                        key={option.key}
                                        value={option.value}
                                        label={option.value}
                                        disabled={disabled}
                                    >
                                        {option.value}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Col>

                    <Col className="filter-control" style={{ marginLeft: 10 }} key={"f-location"}>
                        <span style={{ fontSize: "15px", color: "white" }}>
                            <b>Location</b>
                        </span>
                        <Select style={{ marginLeft: "0px" }} onChange={onProvinceChange} value={filterProvince}>
                            {provinceOptions?.map((option) => {
                                const normalizedOption = normalizeString(option.value);
                                const allNormalized = normalizeString("All Provinces");
                                const disabled = isOptionDisabled(normalizedOption, availableProvinces, allNormalized);
                                return (
                                    <Option key={option.key} value={option.value} disabled={disabled}>
                                        {option.value}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Col>

                    <Col className="filter-button" style={{ marginTop: "25px", paddingTop: "12px", paddingBottom: "12px" }}>
                        <a href="#" className="applyFilter" onClick={(e) => clearFilters(e)}>
                            Clear Filters
                        </a>
                    </Col>
                </Row>
            </Card>

            <div className="pagination">
                {!isEmptyResults && (
                    <Pagination
                        defaultCurrent={1}
                        current={currentPage}
                        defaultPageSize={pageSize}
                        total={total.updated}
                        pageSize={pageSize}
                        showSizeChanger={true}
                        showQuickJumper={true}
                        onChange={onPageChange}
                        onShowSizeChange={onShowSizeChange}
                        pageSizeOptions={[2, 5, 10, 20, 50, 100]}
                        showTotal={(totalVal, range) => `Showing ${range[0]}-${range[1]} of ${totalVal} ${totalVal > 1 ? "Volunteers" : "Volunteer"}`}
                    />
                )}
            </div>

            <div className="join-project-card volunteer-list" style={{ border: "none" }}>
                <div className="parent">
                    {isEmptyResults ? (
                        <Card className="doc-repo-card-empty" bordered={false}>
                            <div className="no-results center-align">
                                <Image src={require("../../assets/no-results.jpg")} alt="Results not found image" preview={false} />
                                <Result title="Sorry,we couldn't find any results, please try again using a different search criteria." />
                            </div>
                        </Card>
                    ) : (
                        <></>
                    )}
                </div>

                <div className="space"></div>
                {loading && (
                    <div style={{
                        textAlign: 'center',
                        padding: '20px'
                    }}>
                        <Spin size="large" />
                    </div>
                )}

                {loading ? (
                    <Skeleton active avatar />
                ) : (
                    <List
                        loading={loading}
                        itemLayout="vertical"
                        pagination={{
                            defaultCurrent: 1,
                            current: currentPage,
                            defaultPageSize: pageSize,
                            total: total.updated,
                            pageSize: pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            onChange: onPageChange,
                            onShowSizeChange: onShowSizeChange,
                            pageSizeOptions: [2, 5, 10, 20, 50, 100],
                            showTotal: (totalVal, range) => `Showing ${range[0]}-${range[1]} of ${totalVal} ${totalVal > 1 ? "Volunteers" : "Volunteer"}`,
                        }}
                        dataSource={filteredData}
                        renderItem={(volunteer) => (
                            <List.Item key={volunteer.key}>
                                <Card className="card-border project-single volunteer-list-card doc-repo-card">
                                    <Row>
                                        <Col xs={24} sm={24} md={5} lg={4} style={{ display: "flex", justifyContent: "center", padding: "0px" }}>
                                            {volunteer.base64 ? (
                                                <img src={`data:${volunteer.type};base64,${volunteer.base64}`} alt="profile" style={{ width: "80%", height: "80%", objectFit: "cover" }} />
                                            ) : (
                                                <UserOutlined style={{ fontSize: "60px", color: "#999" }} />
                                            )}
                                        </Col>

                                        <Col xs={24} sm={24} md={19} lg={20} className="project-details">
                                            <Row>
                                                <Col xs={24} sm={24} md={16}>
                                                    <h2><b>{volunteer.fullname}</b></h2>
                                                </Col>
                                                <Col xs={24} sm={24} md={8} className="right-align">
                                                    <h4><b>{volunteer.location}</b></h4>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={20} sm={20} md={16}>
                                                    <h4><b>{volunteer.fieldOfStudy}</b></h4>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={24} md={24} style={{ wordWrap: "break-word" }}>
                                                    {volunteer.biography}
                                                </Col>
                                            </Row>

                                            <br />
                                            <Row>
                                                <Col xs={24} md={24}>
                                                    <b>Preferred Volunteer Roles:</b> {volunteer.preferredRoles}
                                                </Col>
                                            </Row>
                                            <br />

                                            <Row>
                                                <Col xs={24} md={24}>
                                                    <b>Last updated:</b> {dayjs(volunteer.lastModified).format("DD MMMM YYYY")}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={24} md={24} className="right-align">
                                                    <Link
                                                        key={volunteer.userId}
                                                        to={`/volunteer-scientist/${volunteer.userId}`}
                                                        state={{
                                                            from: location.pathname,
                                                            data: volunteer.userId,
                                                            currentPage: currentPage,
                                                            pageSize: pageSize,
                                                            searchText,
                                                            filterFieldOfStudy,
                                                            filterAreaOfSpecialisation,
                                                            filterAgeRange,
                                                            filterLanguage,
                                                            filterProvince,
                                                        }}
                                                    >
                                                        <Button type="primary">View more details</Button>
                                                    </Link>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Card>
                            </List.Item>
                        )}
                    />
                )}
            </div>
        </div>
    );
}
